import React, { useState, useEffect, useCallback } from 'react';
import DeckGL from '@deck.gl/react';
import { GeoJsonLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { Map } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import './App.css';
import TimeSeriesPopup from './TimeSeriesPopup';
import TimeComparisonPopup from './TimeComparisonPopup';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { I18n } from 'aws-amplify/utils';

// MFAラベルのカスタマイズ
I18n.putVocabulariesForLanguage('en', {
  'Setup TOTP': 'Set up an authenticator app',
  'Confirm TOTP Code': 'Enter the verification code',
  'Account recovery requires verified contact information': 'A verification code will be sent to your email address to complete this process.'
});
I18n.setLanguage('en');

// ============================================
// 定数
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://road-analysis-alb-563909130.ap-south-1.elb.amazonaws.com';

const DATA_TYPES = {
  ROUGHNESS: 'roughness',
  TRAFFIC: 'traffic',
  DECEL: 'decel'
};

// 色設定！E段階�E緁Eオレンジ/赤�E�E
const COLOR_SCALE = [
  [16, 185, 129, 220],
  [245, 158, 11, 220],
  [239, 68, 68, 255]
];

// road_codeごとの色閾値�E�後から調整可能�E�E
// [Good/Fair墁E��, Fair/Poor墁E��]
const THRESHOLDS = {
  DATRL: {
    [DATA_TYPES.ROUGHNESS]: [2000, 4000],
    [DATA_TYPES.TRAFFIC]: [0.00005, 0.0001],
    [DATA_TYPES.DECEL]: [5, 10]
  },
  FRHL: {
    [DATA_TYPES.ROUGHNESS]: [2000, 4000],
    [DATA_TYPES.TRAFFIC]: [0.00005, 0.0001],
    [DATA_TYPES.DECEL]: [5, 10]
  },
  KHEPL: {
    [DATA_TYPES.ROUGHNESS]: [2000, 4000],
    [DATA_TYPES.TRAFFIC]: [0.00005, 0.0001],
    [DATA_TYPES.DECEL]: [5, 10]
  },
  MKTPL: {
    [DATA_TYPES.ROUGHNESS]: [2000, 4000],
    [DATA_TYPES.TRAFFIC]: [0.00005, 0.0001],
    [DATA_TYPES.DECEL]: [5, 10]
  }
};

const TIME_BANDS = [
  { key: 'mid', label: '0:00-6:00 Midnight' },
  { key: 'mor', label: '6:00-10:00 Morning' },
  { key: 'day', label: '10:00-16:00 Daytime' },
  { key: 'eve', label: '16:00-20:00 Evening' },
  { key: 'nig', label: '20:00-24:00 Night' },
];

const TIME_BAND_LABEL = {
  mid: '0:00-6:00 Midnight',
  mor: '6:00-10:00 Morning',
  day: '10:00-16:00 Daytime',
  eve: '16:00-20:00 Evening',
  nig: '20:00-24:00 Night',
};

const getSpeedColor = (speed) => {
  const t = Math.min(1, Math.max(0, ((speed ?? 0) - 30) / 40));
  // 赤(239,68,68) → オレンジ(245,158,11) → 緑(16,185,129) — COLOR_SCALEと同じ色
  let r, g, b;
  if (t < 0.5) {
    const s = t / 0.5;
    r = Math.round(239 + (245 - 239) * s);
    g = Math.round(68  + (158 - 68)  * s);
    b = Math.round(68  + (11  - 68)  * s);
  } else {
    const s = (t - 0.5) / 0.5;
    r = Math.round(245 + (16  - 245) * s);
    g = Math.round(158 + (185 - 158) * s);
    b = Math.round(11  + (129 - 11)  * s);
  }
  return [r, g, b, 220];
};

const DEFAULT_VIEW_STATE = {
  longitude: 77.3,
  latitude: 28.5,
  zoom: 10,
  pitch: 0,
  bearing: 0
};

// backend viewport に対する frontend 側のオフセット（全 road 共通）
const VIEWPORT_OFFSET = {
  zoom: -1,        // ズームレベルのオフセット
  longitude: -0.1,   // 経度のオフセット
  latitude: 0     // 緯度のオフセット
};

// ビューポート調整関数
const adjustViewState = (baseViewState) => {
  if (!baseViewState) return DEFAULT_VIEW_STATE;
  return {
    ...baseViewState,
    zoom: baseViewState.zoom + VIEWPORT_OFFSET.zoom,
    longitude: baseViewState.longitude + VIEWPORT_OFFSET.longitude,
    latitude: baseViewState.latitude + VIEWPORT_OFFSET.latitude
  };
};

// ============================================
// キャチE��ュ付きfetch
// ============================================
const dataCache = {};
const CACHE_DURATION = 5 * 60 * 1000;

const cachedFetch = async (url) => {
  const now = Date.now();
  if (dataCache[url] && now - dataCache[url].ts < CACHE_DURATION) {
    return dataCache[url].data;
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  dataCache[url] = { data, ts: now };
  return data;
};

// ============================================
// App
// ============================================
function App() {
  const [roads, setRoads] = useState([]);
  const [selectedRoad, setSelectedRoad] = useState(null);
  const [activeDataType, setActiveDataType] = useState(DATA_TYPES.ROUGHNESS);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [geoJsonData, setGeoJsonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewState, setViewState] = useState(DEFAULT_VIEW_STATE);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Time Comparison mode
  const [tcMode, setTcMode] = useState(false);
  const [tcRoad, setTcRoad] = useState(null);
  const [tcAvailableMonths, setTcAvailableMonths] = useState([]);
  const [tcRefPeriod, setTcRefPeriod] = useState(null);
  const [tcCompPeriod, setTcCompPeriod] = useState(null);
  const [tcChangeType, setTcChangeType] = useState('Increase');
  const [tcThreshold, setTcThreshold] = useState(1000);
  const [tcResultFeatures, setTcResultFeatures] = useState(null);
  const [tcSelectedSegment, setTcSelectedSegment] = useState(null);
  const [tcLoading, setTcLoading] = useState(false);
  const [tcShowCalled, setTcShowCalled] = useState(false);
  const [showHelpPopup, setShowHelpPopup] = useState(true);

  const [selectedTimeBand, setSelectedTimeBand] = useState('mor');
  const [decelSelectedTimeBand, setDecelSelectedTimeBand] = useState('mor');
  const [trafficTooltip, setTrafficTooltip] = useState(null);
  const [decelTooltip, setDecelTooltip] = useState(null);
  const [showPassageLabels, setShowPassageLabels] = useState(false);
  const [trafficAvailableMonths, setTrafficAvailableMonths] = useState([]);
  const [decelAvailableMonths, setDecelAvailableMonths] = useState({});
  const [trafficSelectedMonth, setTrafficSelectedMonth] = useState(null);
  const [decelSelectedMonth, setDecelSelectedMonth] = useState(null);

  // 起動時: traffic manifest を取得
  useEffect(() => {
    fetch('/traffic/manifest.json')
      .then(r => r.json())
      .then(data => {
        setTrafficAvailableMonths(data.months || []);
        if (data.months && data.months.length > 0) {
          setTrafficSelectedMonth(data.months[data.months.length - 1]);
        }
      })
      .catch(() => {});
  }, []);

  // 起動時: road一覧を取征E
  useEffect(() => {
    cachedFetch(`${API_BASE_URL}/api/roads`)
      .then(data => {
        setRoads(data);
        if (data.length > 0) {
          setSelectedRoad(data[0].road_code);
          setViewState(adjustViewState(data[0].viewport));
        }
      })
      .catch(err => console.error('Failed to fetch roads:', err));
  }, []);

  // road変更時: 利用可能月を取得
  useEffect(() => {
    if (!selectedRoad) return;
    setAvailableMonths([]);
    setSelectedMonth(null);
    setGeoJsonData(null);
    setSelectedSegment(null);

    cachedFetch(`${API_BASE_URL}/api/available-months/${selectedRoad}`)
      .then(data => {
        setAvailableMonths(data.months);
        if (data.months.length > 0) {
          setSelectedMonth(data.months[data.months.length - 1]);
        }
      })
      .catch(err => console.error('Failed to fetch months:', err));

    const roadInfo = roads.find(r => r.road_code === selectedRoad);
    if (roadInfo) setViewState(adjustViewState(roadInfo.viewport));
  }, [selectedRoad, roads]);

  // Hard Braking 月リスト: マニフェストから取得
  useEffect(() => {
    fetch('/decel/manifest.json')
      .then(r => r.json())
      .then(manifest => setDecelAvailableMonths(manifest.roads))
      .catch(() => setDecelAvailableMonths({}));
  }, []);

  // Hard Braking: road変更時にdecel専用月を設定
  useEffect(() => {
    if (!selectedRoad) return;
    const months = decelAvailableMonths[selectedRoad] || [];
    if (months.length > 0) setDecelSelectedMonth(months[months.length - 1]);
  }, [selectedRoad, decelAvailableMonths]);

  // road/月変更晁E GeoJSONを取征E
  useEffect(() => {
    if (!selectedRoad || !selectedMonth) return;

    const load = async () => {
      setLoading(true);
      try {
        // Hard Braking の場合は GeoJSON ファイルから直接読み込み
        if (activeDataType === DATA_TYPES.DECEL) {
          if (!decelSelectedMonth) return;
          const url = `/decel/decel_${selectedRoad}_${decelSelectedMonth}.geojson`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          setGeoJsonData(data);
        } else if (activeDataType === DATA_TYPES.TRAFFIC) {
          // Traffic Volume は月次 GeoJSON ファイルから読み込み
          if (!trafficSelectedMonth) return;
          const url = `/traffic/traffic_${trafficSelectedMonth}.geojson`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          setGeoJsonData(data);
        } else {
          // Road Roughness は API から取得
          const data = await cachedFetch(
            `${API_BASE_URL}/api/road-data/${selectedRoad}/${selectedMonth}`
          );
          setGeoJsonData(data);
        }
      } catch (err) {
        console.error('Failed to fetch road data:', err);
        setGeoJsonData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedRoad, selectedMonth, activeDataType, trafficSelectedMonth, decelSelectedMonth]);

  // TC: tcRoad 変更時に利用可能月を取得 + viewport移動
  useEffect(() => {
    if (!tcRoad) return;
    setTcAvailableMonths([]);
    setTcRefPeriod(null);
    setTcCompPeriod(null);
    setTcResultFeatures(null);
    setTcShowCalled(false);
    cachedFetch(`${API_BASE_URL}/api/available-months/${tcRoad}`)
      .then(data => {
        setTcAvailableMonths(data.months);
        if (data.months.length > 0) {
          setTcRefPeriod(data.months[0]);
          setTcCompPeriod(data.months[data.months.length - 1]);
        }
      })
      .catch(err => console.error('Failed to fetch TC months:', err));
    const tcRoadInfo = roads.find(r => r.road_code === tcRoad);
    if (tcRoadInfo) setViewState(adjustViewState(tcRoadInfo.viewport));
  }, [tcRoad, roads]);

  // 値に基づく色の決宁E
  const getColor = useCallback((value) => {
    const thresholds = THRESHOLDS[selectedRoad]?.[activeDataType] || [2000, 4000];
    if (value >= thresholds[1]) return COLOR_SCALE[2];
    if (value >= thresholds[0]) return COLOR_SCALE[1];
    return COLOR_SCALE[0];
  }, [selectedRoad, activeDataType]);
  // Hard Braking: decel_count に基づく色の決定 (2-4回: 緑, 5-7回: オレンジ, 8+回: 赤)
  const getDecelColor = useCallback((decelCount) => {
    if (decelCount >= 8) return COLOR_SCALE[2];
    if (decelCount >= 5) return COLOR_SCALE[1];
    return COLOR_SCALE[0];
  }, []);

  // TC: セグメントの色
  const getTcColor = (changeDiff) => {
    if (changeDiff > 0) return [239, 68, 68, 255];
    if (changeDiff < 0) return [59, 130, 246, 255];
    return [156, 163, 175, 255];
  };

  // TC: Show Results
  const handleShowResults = async () => {
    if (!tcRoad || !tcRefPeriod || !tcCompPeriod) return;
    setTcLoading(true);
    setTcSelectedSegment(null);
    setTcShowCalled(true);
    try {
      const [refData, compData] = await Promise.all([
        cachedFetch(`${API_BASE_URL}/api/road-data/${tcRoad}/${tcRefPeriod}`),
        cachedFetch(`${API_BASE_URL}/api/road-data/${tcRoad}/${tcCompPeriod}`)
      ]);
      const compMap = {};
      compData.features.forEach(f => { compMap[f.properties.seg_id] = f.properties.rsm; });
      const filtered = [];
      refData.features.forEach(f => {
        const refIndex = f.properties.rsm;
        const compIndex = compMap[f.properties.seg_id];
        if (compIndex === undefined) return;
        const diff = compIndex - refIndex;
        let changeVal;
        if (tcChangeType === 'Increase') changeVal = diff;
        else if (tcChangeType === 'Decrease') changeVal = -diff;
        else changeVal = Math.abs(diff);
        if (changeVal >= tcThreshold) {
          filtered.push({ ...f, properties: { ...f.properties, refIndex, compIndex, changeDiff: diff } });
        }
      });
      setTcResultFeatures(filtered.length > 0 ? { type: 'FeatureCollection', features: filtered } : null);
    } catch (err) {
      console.error('TC Show Results error:', err);
    } finally {
      setTcLoading(false);
    }
  };

  // TC: セグメントクリック
  const handleTcSegmentClick = async (object) => {
    if (!object) return;
    const p = object.properties;
    const { seg_id, refIndex, compIndex, changeDiff } = p;
    const start_km = p.start_km ?? p.seg_start;
    const end_km = p.end_km ?? p.seg_end;
    const road_name = p.road_name ?? roads.find(r => r.road_code === tcRoad)?.road_name;
    const dir = p.dir;
    try {
      const data = await cachedFetch(`${API_BASE_URL}/api/segment-timeseries/${seg_id}`);
      setTcSelectedSegment({ seg_id, refIndex, compIndex, changeDiff, refPeriod: tcRefPeriod, compPeriod: tcCompPeriod, road_name, dir, start_km, end_km, data: data.data });
    } catch (err) {
      setTcSelectedSegment({ seg_id, refIndex, compIndex, changeDiff, refPeriod: tcRefPeriod, compPeriod: tcCompPeriod, road_name, dir, start_km, end_km, data: [] });
    }
  };

  // セグメントクリック: 時系列データ取得
  const handleSegmentClick = async (object) => {
    if (activeDataType === DATA_TYPES.DECEL) return;
    if (!object) return;
    const p = object.properties;
    const { seg_id, rsm, tv, dir } = p;
    // AWS API returns seg_start/seg_end; local API returns start_km/end_km
    const start_km = p.start_km ?? p.seg_start;
    const end_km = p.end_km ?? p.seg_end;
    const road_name = p.road_name ?? roads.find(r => r.road_code === selectedRoad)?.road_name;

    try {
      const data = await cachedFetch(`${API_BASE_URL}/api/segment-timeseries/${seg_id}`);
      setSelectedSegment({
        seg_id,
        currentValue: activeDataType === DATA_TYPES.ROUGHNESS ? rsm : tv,
        road_name, dir, start_km, end_km,
        data: data.data
      });
    } catch (err) {
      console.error('Failed to fetch timeseries:', err);
      setSelectedSegment({ seg_id, currentValue: null, road_name, dir, start_km, end_km, data: [] });
    }
  };

  // Traffic Volume: 時間帯フィルタ済みフィーチャー
  const trafficFeatures = (activeDataType === DATA_TYPES.TRAFFIC && geoJsonData)
    ? geoJsonData.features.filter(f => f.properties.time_band === selectedTimeBand)
    : [];

  // Hard Braking: 時間帯フィルタ済みフィーチャー
  const decelFeatures = (activeDataType === DATA_TYPES.DECEL && geoJsonData)
    ? geoJsonData.features
        .filter(f => f.properties.time_band === decelSelectedTimeBand)
        .sort((a, b) => a.properties.decel_count - b.properties.decel_count)
    : [];

  // レイヤー作成
  const layers = geoJsonData ? (
    activeDataType === DATA_TYPES.DECEL
      ? [
          new GeoJsonLayer({
            id: 'decel-mesh-layer',
            data: { type: 'FeatureCollection', features: decelFeatures },
            pointType: 'circle',
            getFillColor: d => getDecelColor(d.properties.decel_count),
            getLineWidth: 0,
            getPointRadius: 9.36,
            pointRadiusUnits: 'meters',
            pointRadiusMinPixels: 7,
            pointRadiusMaxPixels: 20,
            pickable: true,
            onClick: ({ object, x, y }) => {
              if (object) setDecelTooltip({ feature: object, x, y });
            },
            updateTriggers: {
              getFillColor: [decelSelectedTimeBand],
              data: [decelSelectedTimeBand]
            }
          })
        ]
      : activeDataType === DATA_TYPES.TRAFFIC
      ? [
          new GeoJsonLayer({
            id: 'traffic-layer',
            data: { type: 'FeatureCollection', features: trafficFeatures },
            getLineColor: d => getSpeedColor(d.properties.average_speed_kmh),
            lineWidthUnits: 'pixels',
            getLineWidth: 10,
            widthMinPixels: 2,
            widthMaxPixels: 12,
            pickable: true,
            onHover: ({ object, x, y }) => setTrafficTooltip(object ? { feature: object, x, y } : null),
            updateTriggers: { getLineColor: [selectedTimeBand] }
          }),
          ...(showPassageLabels ? [new TextLayer({
            id: 'traffic-text-layer',
            data: trafficFeatures,
            getPosition: d => {
              const coords = d.geometry.coordinates;
              return coords[Math.floor(coords.length / 2)];
            },
            getText: d => (d.properties.passage_count != null ? d.properties.passage_count : 0).toFixed(1),
            getSize: 12,
            getColor: [0, 0, 0, 220],
            getTextAnchor: 'middle',
            getAlignmentBaseline: 'center',
            fontWeight: 600,
            updateTriggers: { getText: [selectedTimeBand], getPosition: [selectedTimeBand] }
          })] : [])
        ]
      : [
          new GeoJsonLayer({
            id: 'road-layer',
            data: geoJsonData,
            getLineColor: d => getColor(d.properties.rsm),
            lineWidthUnits: 'pixels',
            getLineWidth: d => selectedSegment && d.properties.seg_id === selectedSegment.seg_id ? 14 : 10,
            widthMinPixels: 2,
            widthMaxPixels: 12,
            pickable: true,
            onClick: ({ object }) => handleSegmentClick(object),
            updateTriggers: {
              getLineColor: [selectedRoad, activeDataType],
              getLineWidth: [selectedSegment]
            }
          })
        ]
  ) : [];

  // 月�E表示ラベル
  const formatMonth = (yymm) => {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const yy = parseInt(yymm.slice(0, 2), 10);
    const mm = parseInt(yymm.slice(2), 10);
    return `${monthNames[mm - 1]} ${2000 + yy}`;
  };

  // TC レイヤー
  const tcLayer = tcMode && tcResultFeatures ? [
    new GeoJsonLayer({
      id: 'tc-road-layer',
      data: tcResultFeatures,
      getLineColor: d => getTcColor(d.properties.changeDiff),
      lineWidthUnits: 'pixels',
      getLineWidth: d => tcSelectedSegment && d.properties.seg_id === tcSelectedSegment.seg_id ? 14 : 10,
      widthMinPixels: 2,
      widthMaxPixels: 12,
      pickable: true,
      onClick: ({ object }) => handleTcSegmentClick(object),
      updateTriggers: { getLineColor: [tcResultFeatures], getLineWidth: [tcSelectedSegment] }
    })
  ] : [];

  const activeLayers = tcMode ? tcLayer : layers;

  const selectedRoadInfo = roads.find(r => r.road_code === selectedRoad);
  const tcRoadInfo = roads.find(r => r.road_code === tcRoad);

  return (
    <div style={{ width: '100%', height: '100vh', backgroundColor: '#f1f5f9' }}>
      {/* サイドバー */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: sidebarCollapsed ? '-264px' : '16px',
        zIndex: 10,
        width: '280px',
        transition: 'left 0.3s ease-in-out'
      }}>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{
            position: 'absolute',
            right: '-32px',
            top: '8px',
            width: '32px',
            height: '40px',
            backgroundColor: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderLeft: 'none',
            borderRadius: '0 6px 6px 0',
            color: '#1e293b',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            zIndex: 11
          }}
        >
          {sidebarCollapsed ? '▶' : '◀'}
        </button>

        <div className="legend-panel" style={{ maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
          {!tcMode && (
          <>
            {/* ヘッダー */}
            <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #3B82F6' }}>
              <img src="/company_logo.svg" alt="Company Logo" style={{ width: '64px', height: 'auto', marginBottom: '8px' }} />
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                Road Analysis Dashboard
              </h2>
            </div>
            {/* Road選択 */}
            <div style={{ marginBottom: '4px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                Road
              </label>
            <select
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                color: '#1e293b',
                fontSize: '14px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center'
              }}
              value={selectedRoad || ''}
              onChange={e => {
                setSelectedRoad(e.target.value);
                setSelectedSegment(null);
              }}
            >
              {roads.map(r => (
                <option key={r.road_code} value={r.road_code}>{r.road_code}</option>
              ))}
            </select>
            </div>
            {selectedRoadInfo && (
              <p style={{ margin: '4px 0 16px', fontSize: '11px', color: '#334155' }}>
                {selectedRoadInfo.road_name}
              </p>
            )}

          {/* データタイプ選択 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
              Data Type
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { key: DATA_TYPES.ROUGHNESS, label: 'Road Roughness' },
                { key: DATA_TYPES.TRAFFIC, label: 'Traffic Volume' },
                { key: DATA_TYPES.DECEL, label: 'Hard Braking' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => { setActiveDataType(key); setSelectedSegment(null); setTrafficTooltip(null); }}
                  style={{
                    padding: '12px 14px',
                    border: 'none',
                    borderLeft: activeDataType === key ? '3px solid #3B82F6' : '3px solid transparent',
                    borderRadius: '6px',
                    backgroundColor: activeDataType === key ? 'rgba(37, 99, 235, 0.08)' : '#f8fafc',
                    color: activeDataType === key ? '#3B82F6' : '#334155',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: activeDataType === key ? '600' : 'normal',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: activeDataType === key ? '0 0 12px rgba(59, 130, 246, 0.3)' : 'none'
                  }}
                >
                  <span style={{
                    width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
                    border: activeDataType === key ? '2px solid #3B82F6' : '2px solid #6B7280',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {activeDataType === key && (
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3B82F6' }} />
                    )}
                  </span>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {/* Hard Braking 時間帯選択 */}
          {activeDataType === DATA_TYPES.DECEL && (
            <div style={{ marginTop: '16px', marginBottom: '4px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                Hour of Day (IST)
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {TIME_BANDS.map(({ key, label }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', backgroundColor: decelSelectedTimeBand === key ? 'rgba(37,99,235,0.08)' : 'transparent' }}>
                    <input
                      type="radio"
                      name="decelTimeBand"
                      value={key}
                      checked={decelSelectedTimeBand === key}
                      onChange={() => setDecelSelectedTimeBand(key)}
                      style={{ accentColor: '#3B82F6' }}
                    />
                    <span style={{ fontSize: '12px', color: decelSelectedTimeBand === key ? '#3B82F6' : '#334155', fontWeight: decelSelectedTimeBand === key ? '600' : 'normal' }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {/* Traffic Volume 時間帯選択 */}
          {activeDataType === DATA_TYPES.TRAFFIC && (
            <div style={{ marginTop: '16px', marginBottom: '4px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                Hour of Day (IST)
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {TIME_BANDS.map(({ key, label }) => (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '6px', backgroundColor: selectedTimeBand === key ? 'rgba(37,99,235,0.08)' : 'transparent' }}>
                    <input
                      type="radio"
                      name="timeBand"
                      value={key}
                      checked={selectedTimeBand === key}
                      onChange={() => setSelectedTimeBand(key)}
                      style={{ accentColor: '#3B82F6' }}
                    />
                    <span style={{ fontSize: '12px', color: selectedTimeBand === key ? '#3B82F6' : '#334155', fontWeight: selectedTimeBand === key ? '600' : 'normal' }}>{label}</span>
                  </label>
                ))}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '10px', padding: '6px 8px' }}>
                <input
                  type="checkbox"
                  checked={showPassageLabels}
                  onChange={e => setShowPassageLabels(e.target.checked)}
                  style={{ accentColor: '#3B82F6', width: '14px', height: '14px' }}
                />
                <span style={{ fontSize: '12px', color: '#334155' }}>Show passage count labels</span>
              </label>
            </div>
          )}
          <div style={{ borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

          {/* 月選抁E*/}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
              Month
            </label>
            <select
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: '#f8fafc',
                color: '#1e293b',
                fontSize: '14px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center'
              }}
              value={activeDataType === DATA_TYPES.TRAFFIC ? (trafficSelectedMonth || '') : activeDataType === DATA_TYPES.DECEL ? (decelSelectedMonth || '') : (selectedMonth || '')}
              onChange={e => {
                if (activeDataType === DATA_TYPES.TRAFFIC) {
                  setTrafficSelectedMonth(e.target.value);
                } else if (activeDataType === DATA_TYPES.DECEL) {
                  setDecelSelectedMonth(e.target.value);
                } else {
                  setSelectedMonth(e.target.value);
                  setSelectedSegment(null);
                }
              }}
              disabled={activeDataType === DATA_TYPES.TRAFFIC ? trafficAvailableMonths.length === 0 : activeDataType === DATA_TYPES.DECEL ? (decelAvailableMonths[selectedRoad] || []).length === 0 : availableMonths.length === 0}
            >
              {(activeDataType === DATA_TYPES.TRAFFIC ? trafficAvailableMonths : activeDataType === DATA_TYPES.DECEL ? (decelAvailableMonths[selectedRoad] || []) : availableMonths).map(m => (
                <option key={m} value={m}>{formatMonth(m)}</option>
              ))}
            </select>
          </div>

          <div style={{ borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

          {/* 凡侁E*/}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>
              Legend
            </label>
            {activeDataType === DATA_TYPES.DECEL ? (
              // Hard Braking: メッシュを横並び
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', justifyContent: 'center' }}>
                {COLOR_SCALE.map((color, index) => (
                  <div key={index} style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    ></div>
                    <div style={{ fontSize: '11px', marginTop: '4px', color: '#334155' }}>
                      {index === 0 ? '2-4' : index === 1 ? '5-7' : '8+'}
                    </div>
                  </div>
                ))}
              </div>
            ) : activeDataType === DATA_TYPES.TRAFFIC ? (
              // Traffic Volume: 速度グラデーション
              <>
                <div style={{ background: 'linear-gradient(to right, rgb(239,68,68), rgb(245,158,11), rgb(16,185,129))', height: '20px', borderRadius: '4px' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  {[30, 38, 46, 54, 62, 70].map(v => (
                    <span key={v} style={{ fontSize: '10px', color: '#64748b' }}>{v}</span>
                  ))}
                </div>
                <div style={{ marginTop: '6px', fontSize: '10px', color: '#334155', lineHeight: '1.4' }}>
                  Average Speed (km/h)
                </div>
              </>
            ) : (
              // Road Roughness: 横長バー
              <>
                <div style={{ display: 'flex', height: '20px', borderRadius: '4px', overflow: 'hidden' }}>
                  {COLOR_SCALE.map((color, i) => (
                    <div key={i} style={{
                      flex: 1,
                      backgroundColor: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`
                    }} />
                  ))}
                </div>
                <div style={{ display: 'flex', marginTop: '6px' }}>
                  {['Good', 'Fair', 'Poor'].map(l => (
                    <span key={l} style={{ flex: 1, fontSize: '11px', color: '#334155', fontWeight: '500', textAlign: 'center' }}>{l}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          {loading && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>Loading...</span>
            </div>
          )}

          {/* Time Comparison ボタン (最下部) */}
          <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
            <button
              onClick={() => {
                setTcMode(true);
                setSelectedSegment(null);
                setTcRoad(selectedRoad);
              }}
              style={{
                width: '100%', padding: '12px', border: '1px solid #3B82F6', borderRadius: '6px',
                cursor: 'pointer', backgroundColor: 'transparent', color: '#2563eb',
                fontSize: '13px', fontWeight: '600', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              Period Comparison
            </button>
          </div>
          </>
          )}

          {/* Time Comparison サイドバー */}
          {tcMode && (
          <>
            {/* ヘッダー */}
            <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #3B82F6' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                Road Roughness<br />Period Comparison
              </h2>
            </div>

            {/* Road */}
            <div style={{ marginBottom: '4px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>Road</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px',
                  backgroundColor: '#f8fafc', color: '#1e293b', fontSize: '14px', cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'
                }}
                value={tcRoad || ''}
                onChange={e => { setTcRoad(e.target.value); }}
              >
                {roads.map(r => (<option key={r.road_code} value={r.road_code}>{r.road_code}</option>))}
              </select>
            </div>
            {tcRoadInfo && (
              <p style={{ margin: '4px 0 16px', fontSize: '11px', color: '#334155' }}>
                {tcRoadInfo.road_name}
              </p>
            )}

            <div style={{ borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

            {/* Reference Period */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>Reference Period</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px',
                  backgroundColor: '#f8fafc', color: '#1e293b', fontSize: '14px', cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'
                }}
                value={tcRefPeriod || ''}
                onChange={e => setTcRefPeriod(e.target.value)}
                disabled={tcAvailableMonths.length === 0}
              >
                {tcAvailableMonths.map(m => (<option key={m} value={m}>{formatMonth(m)}</option>))}
              </select>
            </div>

            {/* Comparison Period */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>Comparison Period</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px',
                  backgroundColor: '#f8fafc', color: '#1e293b', fontSize: '14px', cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'
                }}
                value={tcCompPeriod || ''}
                onChange={e => setTcCompPeriod(e.target.value)}
                disabled={tcAvailableMonths.length === 0}
              >
                {tcAvailableMonths.map(m => (<option key={m} value={m}>{formatMonth(m)}</option>))}
              </select>
            </div>

            {/* Change Type */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>Change Type</label>
              <select
                style={{
                  width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px',
                  backgroundColor: '#f8fafc', color: '#1e293b', fontSize: '14px', cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center'
                }}
                value={tcChangeType}
                onChange={e => setTcChangeType(e.target.value)}
              >
                <option value="Increase">Increase</option>
                <option value="Decrease">Decrease</option>
                <option value="Absolute Change">Absolute Change</option>
              </select>
            </div>

            {/* Threshold */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>Threshold</label>
              <input
                type="number"
                min={0}
                step={1}
                value={tcThreshold}
                onChange={e => {
                  const v = Math.max(0, Math.floor(Number(e.target.value)));
                  setTcThreshold(v);
                }}
                style={{
                  width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px',
                  backgroundColor: '#f8fafc', color: '#1e293b', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Show Results */}
            <button
              onClick={handleShowResults}
              disabled={tcLoading || !tcRefPeriod || !tcCompPeriod}
              style={{
                width: '100%', padding: '12px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                backgroundColor: '#3B82F6', color: '#ffffff', fontSize: '14px', fontWeight: '600',
                opacity: (tcLoading || !tcRefPeriod || !tcCompPeriod) ? 0.5 : 1,
                marginBottom: '16px'
              }}
            >
              {tcLoading ? 'Loading...' : 'Show Results'}
            </button>

            {tcShowCalled && !tcLoading && !tcResultFeatures && (
              <div style={{ padding: '10px 12px', backgroundColor: 'rgba(239,68,68,0.06)', borderRadius: '6px', borderLeft: '3px solid #EF4444' }}>
                <span style={{ fontSize: '12px', color: '#b91c1c' }}>No segments found matching the criteria. Try adjusting the threshold or selecting different periods.</span>
              </div>
            )}

            <div style={{ borderTop: '1px solid #e2e8f0', margin: '16px 0' }} />

            {/* Legend */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#334155', fontWeight: '500' }}>Legend</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { color: '#EF4444', label: 'Increase' },
                  { color: '#2563eb', label: 'Decrease' },
                  { color: '#64748b', label: 'No Change' }
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '28px', height: '6px', backgroundColor: color, borderRadius: '3px', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: '#334155' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 戻るボタン (最下部) */}
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-start' }}>
              <button
                onClick={() => {
                  setTcMode(false);
                  setTcSelectedSegment(null);
                  setSelectedRoad(tcRoad);
                }}
                style={{
                  width: '36px', height: '36px', border: '1px solid #e2e8f0', borderRadius: '6px',
                  cursor: 'pointer', backgroundColor: '#f8fafc', color: '#1e293b',
                  fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#e2e8f0'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                title="Back to Current"
              >
                ←
              </button>
            </div>
          </>
          )}
        </div>
      </div>

      {/* 地図 */}
      <DeckGL
        viewState={viewState}
        onViewStateChange={({ viewState: vs }) => setViewState(vs)}
        controller={true}
        layers={activeLayers}
        style={{ width: '100vw', height: '100vh' }}
      >
        <Map mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json" />
      </DeckGL>

      {/* ヘルプポップアップ */}
      {showHelpPopup && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '16px',
          zIndex: 1000,
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px',
          width: '280px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#1e293b', fontWeight: '600' }}>
              Tip
            </p>
            <button
              onClick={() => setShowHelpPopup(false)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#64748b',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>
          <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
            To view the time series graph for Road Roughness, click a road segment.
          </p>
        </div>
      )}

      {/* クレジチE�� */}
      <div style={{
        position: 'fixed', bottom: '0', right: '0', zIndex: 9999,
        backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: '2px 5px',
        fontSize: '12px', fontFamily: 'Helvetica, Arial, sans-serif',
        borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }}>
        <a href="https://www.maplibre.org/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(0,0,0,0.75)', textDecoration: 'none', marginRight: '3px' }}>MapLibre</a>
        <span style={{ color: 'rgba(0,0,0,0.75)', margin: '0 3px' }}>|</span>
        <a href="https://carto.com/about-carto/" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(0,0,0,0.75)', textDecoration: 'none', margin: '0 3px' }}>© CARTO</a>
        <span style={{ color: 'rgba(0,0,0,0.75)', margin: '0 3px' }}>|</span>
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" style={{ color: 'rgba(0,0,0,0.75)', textDecoration: 'none', marginLeft: '3px' }}>© OpenStreetMap contributors</a>
      </div>

      {/* 時系列ポップアップ (通常モード) */}
      {!tcMode && selectedSegment && (
        <>
          <div
            onClick={() => setSelectedSegment(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999
            }}
          />
          <TimeSeriesPopup
            segment={selectedSegment}
            dataType={activeDataType}
            roadCode={selectedRoad}
            thresholds={THRESHOLDS}
            onClose={() => setSelectedSegment(null)}
          />
        </>
      )}

      {/* Hard Braking クリックポップアップ */}
      {activeDataType === DATA_TYPES.DECEL && decelTooltip && (
        <div
          style={{
            position: 'fixed',
            left: decelTooltip.x + 12,
            top: decelTooltip.y + 12,
            zIndex: 1001,
            backgroundColor: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '200px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontWeight: '600', color: '#1e293b' }}>Hard Braking</span>
            <span
              onClick={() => setDecelTooltip(null)}
              style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '16px', lineHeight: 1 }}
            >×</span>
          </div>
          <div><span style={{ color: '#64748b' }}>road_code: </span>{decelTooltip.feature.properties.road_code}</div>
          <div><span style={{ color: '#64748b' }}>yymm: </span>{decelTooltip.feature.properties.yymm}</div>
          <div><span style={{ color: '#64748b' }}>direction: </span>{decelTooltip.feature.properties.direction}</div>
          <div><span style={{ color: '#64748b' }}>decel_count: </span>{decelTooltip.feature.properties.decel_count}</div>
          <div><span style={{ color: '#64748b' }}>dec_g: </span>{decelTooltip.feature.properties.dec_g}</div>
          <div><span style={{ color: '#64748b' }}>speed_kmh: </span>{decelTooltip.feature.properties.speed_kmh} km/h</div>
        </div>
      )}

      {/* Traffic Volume ホバーツールチップ */}
      {activeDataType === DATA_TYPES.TRAFFIC && trafficTooltip && (
        <div style={{
          position: 'fixed',
          left: trafficTooltip.x + 12,
          top: trafficTooltip.y + 12,
          zIndex: 1001,
          backgroundColor: '#ffffff',
          border: '1px solid #cbd5e1',
          borderRadius: '8px',
          padding: '10px 14px',
          pointerEvents: 'none',
          fontSize: '13px',
          color: '#1e293b',
          minWidth: '200px',
          lineHeight: '1.7',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
        }}>
          <div><span style={{ color: '#64748b' }}>segment_start: </span>{trafficTooltip.feature.properties.start_km}</div>
          <div><span style={{ color: '#64748b' }}>segment_end: </span>{trafficTooltip.feature.properties.end_km}</div>
          <div><span style={{ color: '#64748b' }}>time: </span>{TIME_BAND_LABEL[trafficTooltip.feature.properties.time_band] ?? trafficTooltip.feature.properties.time_band}</div>
          <div><span style={{ color: '#64748b' }}>passage_count: </span>{trafficTooltip.feature.properties.passage_count}</div>
          <div><span style={{ color: '#64748b' }}>average_speed: </span>{trafficTooltip.feature.properties.average_speed_kmh} km/h</div>
        </div>
      )}

      {/* TC モード: 時系列 + 比較ポップアップ */}
      {tcMode && tcSelectedSegment && (
        <>
          <div
            onClick={() => setTcSelectedSegment(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999
            }}
          />
          <TimeSeriesPopup
            segment={tcSelectedSegment}
            dataType={DATA_TYPES.ROUGHNESS}
            roadCode={tcRoad}
            thresholds={THRESHOLDS}
            onClose={() => setTcSelectedSegment(null)}
            positionStyle={{ position: 'fixed', top: '50%', left: '35%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}
          />
          <TimeComparisonPopup
            segment={tcSelectedSegment}
            onClose={() => setTcSelectedSegment(null)}
          />
        </>
      )}
    </div>
  );
}

function AppWithAuth() {
  return (
    <Authenticator hideSignUp={true}>
      {({ signOut, user }) => <App signOut={signOut} user={user} />}
    </Authenticator>
  );
}

export default AppWithAuth;
